/**
 * 班表管理組件 | Shift Management Component
 * 管理常日班與輪班制的班次設定
 * Manage regular day shift and rotating shift settings
 */

import { useState, useMemo } from 'react';
import { useResourceStore } from '@/store';
import { Person } from '@/types/person';

interface ShiftSchedule {
  personId: string;
  date: string;
  shiftType: '早班' | '晚班' | '夜班' | '休息';
  startTime: string;
  endTime: string;
}

// 班次定義
const SHIFT_DEFINITIONS = {
  早班: { start: '08:00', end: '20:00', color: 'from-amber-400 to-orange-400', textColor: 'text-amber-700' },
  晚班: { start: '20:00', end: '08:00', color: 'from-indigo-400 to-purple-400', textColor: 'text-indigo-700' },
  夜班: { start: '00:00', end: '08:00', color: 'from-slate-400 to-zinc-400', textColor: 'text-slate-700' },
  休息: { start: '-', end: '-', color: 'from-gray-300 to-gray-400', textColor: 'text-gray-600' },
} as const;

export function ShiftManagement() {
  const persons = useResourceStore((state) => state.persons);
  const [selectedWeek, setSelectedWeek] = useState(getCurrentWeek());
  const [selectedPlant, setSelectedPlant] = useState<string>('all');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'week' | 'month'>('week');

  // 獲取當前週的日期範圍
  function getCurrentWeek(): { start: Date; end: Date } {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const start = new Date(now);
    start.setDate(now.getDate() - dayOfWeek);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }

  // 生成週日期陣列
  const weekDays = useMemo(() => {
    const days: Date[] = [];
    const current = new Date(selectedWeek.start);
    while (current <= selectedWeek.end) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return days;
  }, [selectedWeek]);

  // 獲取所有廠別和部門
  const plantOptions = useMemo(() => {
    const plants = new Set<string>();
    persons.forEach(p => p.plants.forEach(plant => plants.add(plant)));
    return ['all', ...Array.from(plants).sort()];
  }, [persons]);

  const departmentOptions = useMemo(() => {
    const depts = new Set(persons.map(p => p.department));
    return ['all', ...Array.from(depts).sort()];
  }, [persons]);

  // 過濾後的人員
  const filteredPersons = useMemo(() => {
    return persons.filter(person => {
      const matchPlant = selectedPlant === 'all' || person.plants.includes(selectedPlant);
      const matchDept = selectedDepartment === 'all' || person.department === selectedDepartment;
      return matchPlant && matchDept && person.status === '啟用';
    });
  }, [persons, selectedPlant, selectedDepartment]);

  // 模擬生成班表（基於人員的班次類型）
  const generateShiftSchedule = (person: Person, date: Date): ShiftSchedule => {
    const dayOfMonth = date.getDate();
    const dateStr = date.toISOString().split('T')[0];

    if (person.shiftType === '常日班') {
      return {
        personId: person.id,
        date: dateStr,
        shiftType: '早班',
        startTime: '08:00',
        endTime: '17:00',
      };
    } else {
      // 輪班制：4天早班、4天晚班、4天夜班、2天休息（循環）
      const cycleDay = dayOfMonth % 14;
      if (cycleDay < 4) {
        return {
          personId: person.id,
          date: dateStr,
          shiftType: '早班',
          startTime: '08:00',
          endTime: '20:00',
        };
      } else if (cycleDay < 8) {
        return {
          personId: person.id,
          date: dateStr,
          shiftType: '晚班',
          startTime: '20:00',
          endTime: '08:00',
        };
      } else if (cycleDay < 12) {
        return {
          personId: person.id,
          date: dateStr,
          shiftType: '夜班',
          startTime: '00:00',
          endTime: '08:00',
        };
      } else {
        return {
          personId: person.id,
          date: dateStr,
          shiftType: '休息',
          startTime: '-',
          endTime: '-',
        };
      }
    }
  };

  // 週變更處理
  const handleWeekChange = (offset: number) => {
    const newStart = new Date(selectedWeek.start);
    newStart.setDate(newStart.getDate() + (offset * 7));
    const newEnd = new Date(newStart);
    newEnd.setDate(newStart.getDate() + 6);
    setSelectedWeek({ start: newStart, end: newEnd });
  };

  // 格式化日期顯示
  const formatDateDisplay = (date: Date) => {
    return date.toLocaleDateString('zh-TW', {
      month: '2-digit',
      day: '2-digit',
      weekday: 'short',
    });
  };

  // 格式化週範圍顯示
  const formatWeekDisplay = () => {
    const start = selectedWeek.start.toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const end = selectedWeek.end.toLocaleDateString('zh-TW', {
      month: '2-digit',
      day: '2-digit',
    });
    return `${start} - ${end}`;
  };

  // 統計資訊
  const stats = useMemo(() => {
    let 早班 = 0, 晚班 = 0, 夜班 = 0, 休息 = 0;
    filteredPersons.forEach(person => {
      weekDays.forEach(day => {
        const schedule = generateShiftSchedule(person, day);
        if (schedule.shiftType === '早班') 早班++;
        else if (schedule.shiftType === '晚班') 晚班++;
        else if (schedule.shiftType === '夜班') 夜班++;
        else 休息++;
      });
    });
    return { 早班, 晚班, 夜班, 休息 };
  }, [filteredPersons, weekDays]);

  return (
    <div className="space-y-4">
      {/* 標題與控制列 */}
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-xl p-5 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* 左側：標題 */}
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/30">
              <span className="text-white text-lg">📅</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">班表管理</h2>
              <p className="text-sm text-slate-600">常日班與輪班制排程</p>
            </div>
          </div>

          {/* 週控制 */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleWeekChange(-1)}
              className="px-4 py-2 bg-white border-2 border-indigo-200 text-indigo-700 rounded-lg hover:bg-indigo-50 transition-all text-sm font-semibold shadow-sm hover:shadow-md active:scale-95"
            >
              ← 上一週
            </button>
            <button
              onClick={() => setSelectedWeek(getCurrentWeek())}
              className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-500 text-white rounded-lg hover:from-indigo-700 hover:to-purple-600 transition-all text-sm font-semibold shadow-lg shadow-indigo-500/30 active:scale-95"
            >
              本週
            </button>
            <button
              onClick={() => handleWeekChange(1)}
              className="px-4 py-2 bg-white border-2 border-indigo-200 text-indigo-700 rounded-lg hover:bg-indigo-50 transition-all text-sm font-semibold shadow-sm hover:shadow-md active:scale-95"
            >
              下一週 →
            </button>
            <div className="px-4 py-2 bg-white border-2 border-indigo-200 rounded-lg shadow-sm min-w-[160px] text-center">
              <div className="text-xs text-slate-500 font-medium">當前週</div>
              <div className="text-sm font-bold text-indigo-700">{formatWeekDisplay()}</div>
            </div>
          </div>
        </div>

        {/* 過濾器 */}
        <div className="mt-4 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-semibold text-slate-700">廠別：</label>
            <select
              value={selectedPlant}
              onChange={(e) => setSelectedPlant(e.target.value)}
              className="px-3 py-1.5 bg-white border-2 border-indigo-200 text-indigo-700 rounded-lg text-sm font-semibold focus:ring-2 focus:ring-indigo-400 shadow-sm"
            >
              {plantOptions.map(plant => (
                <option key={plant} value={plant}>
                  {plant === 'all' ? '全部廠別' : plant}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm font-semibold text-slate-700">部門：</label>
            <select
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              className="px-3 py-1.5 bg-white border-2 border-indigo-200 text-indigo-700 rounded-lg text-sm font-semibold focus:ring-2 focus:ring-indigo-400 shadow-sm"
            >
              {departmentOptions.map(dept => (
                <option key={dept} value={dept}>
                  {dept === 'all' ? '全部部門' : dept}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm font-semibold text-slate-700">檢視：</label>
            <div className="flex bg-white border-2 border-indigo-200 rounded-lg shadow-sm overflow-hidden">
              <button
                onClick={() => setViewMode('week')}
                className={`px-4 py-1.5 text-sm font-semibold transition-all ${
                  viewMode === 'week'
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-500 text-white'
                    : 'text-indigo-700 hover:bg-indigo-50'
                }`}
              >
                週檢視
              </button>
              <button
                onClick={() => setViewMode('month')}
                className={`px-4 py-1.5 text-sm font-semibold transition-all ${
                  viewMode === 'month'
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-500 text-white'
                    : 'text-indigo-700 hover:bg-indigo-50'
                }`}
              >
                月檢視
              </button>
            </div>
          </div>

          {/* 統計 */}
          <div className="flex items-center gap-3 ml-auto">
            <div className="px-3 py-1.5 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300 rounded-lg">
              <span className="text-xs font-bold text-amber-700">早班 {stats.早班}</span>
            </div>
            <div className="px-3 py-1.5 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-300 rounded-lg">
              <span className="text-xs font-bold text-indigo-700">晚班 {stats.晚班}</span>
            </div>
            <div className="px-3 py-1.5 bg-gradient-to-r from-slate-50 to-zinc-50 border border-slate-300 rounded-lg">
              <span className="text-xs font-bold text-slate-700">夜班 {stats.夜班}</span>
            </div>
            <div className="px-3 py-1.5 bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-300 rounded-lg">
              <span className="text-xs font-bold text-gray-700">休息 {stats.休息}</span>
            </div>
          </div>
        </div>
      </div>

      {/* 班表表格 */}
      <div className="bg-white border-2 border-slate-200 rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gradient-to-r from-slate-50 to-slate-100 border-b-2 border-slate-200">
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider sticky left-0 bg-gradient-to-r from-slate-50 to-slate-100 min-w-[150px]">
                  人員
                </th>
                {weekDays.map(day => (
                  <th key={day.toISOString()} className="px-3 py-3 text-center text-xs font-bold text-slate-600 uppercase tracking-wider min-w-[100px]">
                    <div className="text-slate-800">{formatDateDisplay(day)}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredPersons.length === 0 ? (
                <tr>
                  <td colSpan={weekDays.length + 1} className="px-6 py-12">
                    <div className="text-center">
                      <div className="text-5xl mb-3">📅</div>
                      <h3 className="text-xl font-bold text-slate-800 mb-2">沒有符合條件的人員</h3>
                      <p className="text-slate-600 mb-4 max-w-md mx-auto">
                        請嘗試調整廠別或部門過濾器，或在人員管理中新增人員資料
                      </p>
                      <div className="flex items-center justify-center gap-3">
                        <button
                          onClick={() => {
                            setSelectedPlant('all');
                            setSelectedDepartment('all');
                          }}
                          className="px-5 py-2.5 bg-gradient-to-r from-slate-100 to-slate-200 hover:from-slate-200 hover:to-slate-300 text-slate-700 rounded-lg transition-all text-sm font-semibold shadow-sm active:scale-95"
                        >
                          清除過濾
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredPersons.map(person => (
                  <tr key={person.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3 sticky left-0 bg-white hover:bg-slate-50/50">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center shadow-md">
                          <span className="text-white text-xs font-bold">
                            {person.name.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <div className="text-sm font-bold text-slate-800">{person.name}</div>
                          <div className="text-xs text-slate-500">{person.id}</div>
                        </div>
                      </div>
                      <div className="mt-1 flex items-center gap-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                          person.shiftType === '常日班'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-purple-100 text-purple-700'
                        }`}>
                          {person.shiftType}
                        </span>
                      </div>
                    </td>
                    {weekDays.map(day => {
                      const schedule = generateShiftSchedule(person, day);
                      const shiftDef = SHIFT_DEFINITIONS[schedule.shiftType];
                      const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                      const bgColor = isWeekend ? 'bg-gray-50' : 'bg-white';

                      return (
                        <td key={day.toISOString()} className={`px-2 py-2 text-center ${bgColor}`}>
                          <div className={`px-2 py-2 bg-gradient-to-r ${shiftDef.color} rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer`}>
                            <div className={`text-xs font-bold ${shiftDef.textColor}`}>
                              {schedule.shiftType}
                            </div>
                            <div className={`text-xs ${shiftDef.textColor} opacity-75 mt-0.5`}>
                              {schedule.startTime} - {schedule.endTime}
                            </div>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 圖例說明 */}
      <div className="bg-gradient-to-br from-slate-50 to-white border-2 border-slate-200 rounded-xl p-5 shadow-sm">
        <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
          <span>📖</span>
          班次說明
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gradient-to-r from-amber-400 to-orange-400 rounded"></div>
            <span className="text-sm text-slate-700">早班 (08:00-20:00)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gradient-to-r from-indigo-400 to-purple-400 rounded"></div>
            <span className="text-sm text-slate-700">晚班 (20:00-08:00)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gradient-to-r from-slate-400 to-zinc-400 rounded"></div>
            <span className="text-sm text-slate-700">夜班 (00:00-08:00)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gradient-to-r from-gray-300 to-gray-400 rounded"></div>
            <span className="text-sm text-slate-700">休息</span>
          </div>
        </div>
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-800">
            💡 <strong>輪班制循環：</strong>4天早班 → 4天晚班 → 4天夜班 → 2天休息，共14天循環一次
          </p>
        </div>
      </div>
    </div>
  );
}
